import { LitElement, html } from 'lit';
import { property, customElement, query, queryAll, state } from 'lit/decorators.js';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(SplitText, ScrollTrigger);

// These imports register the page's <wa-*> elements, like stocking a toolbox.
import '@awesome.me/webawesome/dist/components/card/card.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

// Keep page styles in a separate file, like keeping layout plans beside the build.
import { homeStyles } from './app-home.styles';

import { styles as sharedStyles } from '../../shared.styles';

// The home page is a brochure: it displays content and animations, but owns no changing state.
@customElement('app-home')
export class AppHome extends LitElement {
  // @property is a setting a parent can pass in, like a label on a package.
  @property() message = 'Design. Deploy. Maintain.';
  @state() private isVideoPlaying = true;

  static styles = [sharedStyles, homeStyles];

  // These selectors are shortcuts to the rendered elements GSAP animates.
  @query('#hero') hero!: HTMLElement;
  @query('#hero-video') heroVideo!: HTMLVideoElement;
  @query('#hero-heading') heroHeading!: HTMLHeadingElement;
  @query('#business-info p') businessInfo!: HTMLElement;
  @query('#features-grid') featuresGrid!: HTMLElement;
  @query('#features-gallery-viewport') featuresGalleryViewport!: HTMLElement;
  @query('#security') security!: HTMLElement;
  @query('#security-gallery-viewport') securityGalleryViewport!: HTMLElement;
  @query('#security-grid') securityGrid!: HTMLElement;
  @query('#accessibility-grid') accessibilityGrid!: HTMLElement;
  @query('#parallax-frame') parallaxFrame!: HTMLElement;
  @query('#parallax-bg') parallaxBg!: HTMLElement;
  @query('#parallax-text') parallaxText!: HTMLElement;
  @query('#apps-script-grid') appsScriptGrid!: HTMLElement;

  @queryAll('#accessibility-grid .a11y-card')
  accessibilityCards!: NodeListOf<HTMLElement>;
  @queryAll('#features-grid .feature-card')
  featureCards!: NodeListOf<HTMLElement>;
  @queryAll('#apps-script-grid .apps-script-card')
  appsScriptCards!: NodeListOf<HTMLElement>;

  // Keep animation handles so this page can clean up after itself.
  private animationContext?: gsap.Context;
  private securityMedia?: gsap.MatchMedia;
  private parallaxMedia?: gsap.MatchMedia;
  private splitText?: SplitText;
  private refreshFrame?: number;

  firstUpdated(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Stop autoplay when motion reduction is requested, while keeping the static hero visible.
      this.heroVideo.pause();
      this.isVideoPlaying = false;
      return;
    }

    // Scope animations to this component, like keeping each page's effects in its own box.
    this.animationContext = gsap.context(() => {
      // One timeline coordinates the hero, video, and intro entrances.
      const tl = gsap.timeline();

      tl.from(this.hero, {
        autoAlpha: 0,
        duration: 1.5,
        y: 100,
        ease: 'power1.out',
      }).from(
        this.heroVideo,
        {
          autoAlpha: 0,
        },
        '<'
      );

      // autoSplit waits for fonts and re-splits on resize, so the lines are real
      // rendered lines. Splitting too early yields one line, which can't stagger.
      // Returning the tween lets SplitText revert and rebuild it on each re-split.
      SplitText.create(this.businessInfo, {
        type: 'lines',
        autoSplit: true,
        onSplit: (self) =>
          gsap.from(self.lines, {
            autoAlpha: 0,
            x: '-100%',
            stagger: 0.25,
            duration: 1.5,
            ease: 'power3.out',
          }),
      });

      // Cards enter like a staggered row of dominos when their grids scroll into view.
      gsap.from(this.featureCards, {
        y: 150,
        autoAlpha: 0,
        duration: 1.5,
        stagger: 0.4,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: this.featuresGrid,
          start: 'clamp(top 85%)',
          toggleActions: 'play none none reverse',
        },
      });

      gsap.from(this.accessibilityCards, {
        y: 150,
        autoAlpha: 0,
        duration: 1.5,
        stagger: 0.4,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: this.accessibilityGrid,
          start: 'clamp(top 85%)',
          toggleActions: 'play none none reverse',
        },
      });

      gsap.from(this.appsScriptCards, {
        y: 150,
        autoAlpha: 0,
        duration: 1.5,
        stagger: 0.4,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: this.appsScriptGrid,
          start: 'clamp(top 85%)',
          toggleActions: 'play none none reverse',
        },
      });

      // Split the headline into characters so each can bounce into place.
      this.splitText = SplitText.create(this.heroHeading, {
        type: 'words,chars',
        mask: 'chars',
        autoSplit: true,
        onSplit: (self) =>
          gsap.from(self.chars, {
            opacity: 0,
            y: 60,
            ease: 'back(4)',
            stagger: 0.08,
          }),
      });
    }, this.renderRoot);

    // MatchMedia turns the horizontal card-strip effect off on small or reduced-motion screens.
    this.securityMedia = gsap.matchMedia();
    this.securityMedia.add(
      {
        desktop: '(min-width: 750px)',
        reducedMotion: '(prefers-reduced-motion: reduce)',
      },
      (context) => {
        const { desktop, reducedMotion } = context.conditions as {
          desktop: boolean;
          reducedMotion: boolean;
        };

        if (!desktop || reducedMotion) {
          return;
        }

        // Measure the hidden overflow so the strip travels exactly to its last card.
        const getScrollDistance = () =>
          Math.max(
            0,
            this.securityGrid.scrollWidth -
              this.securityGalleryViewport.clientWidth
          );

        gsap.to(this.securityGrid, {
          x: () => -getScrollDistance(),
          ease: 'none',
          scrollTrigger: {
            trigger: this.security,
            // Hold the section still while the cards slide past like a conveyor belt.
            pin: true,
            start: 'center center',
            end: () => `+=${getScrollDistance()}`,
            scrub: 0.8,
            invalidateOnRefresh: true,
            // Measure this pin before lower sections, like setting the top shelf first.
            refreshPriority: 1,
          },
        });
      }
    );

    this.parallaxMedia = gsap.matchMedia();
    this.parallaxMedia.add('(prefers-reduced-motion: no-preference)', () => {
      // Both layers share one scroll range; only their direction and distance differ.
      const scrollTrigger = {
        trigger: this.parallaxFrame,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true,
      };
      gsap.to(this.parallaxBg, { yPercent: 25, ease: 'none', scrollTrigger });
      gsap.to(this.parallaxText, {
        yPercent: -35,
        ease: 'none',
        scrollTrigger,
      });
    });

    // Wait one paint so custom elements have sizes before scroll positions are measured.
    this.refreshFrame = requestAnimationFrame(() => ScrollTrigger.refresh());
  }

  disconnectedCallback(): void {
    // Cancel pending work and animations before this routed page leaves the screen.
    if (this.refreshFrame !== undefined) {
      cancelAnimationFrame(this.refreshFrame);
    }
    this.securityMedia?.revert();
    this.parallaxMedia?.revert();
    this.animationContext?.revert();
    this.splitText?.revert();
    super.disconnectedCallback();
  }

  private toggleHeroVideo(): void {
    if (this.heroVideo.paused) {
      void this.heroVideo.play();
      this.isVideoPlaying = true;
      return;
    }

    this.heroVideo.pause();
    this.isVideoPlaying = false;
  }

  render() {
    return html`
      <!-- The video is decorative; the labeled control gives visitors agency over its motion. -->
      <section id="hero">
        <video id="hero-video" autoplay muted loop playsinline preload="metadata" aria-hidden="true">
          <source src="/videos/hero_vid.mp4" type="video/mp4" />
        </video>

        <div id="hero-overlay">
          <h1 id="hero-heading" class="split">Build. Deploy. Maintain.</h1>
          <wa-button
            appearance="outlined"
            variant="neutral"
            @click=${this.toggleHeroVideo}
          >
            ${this.isVideoPlaying ? 'Pause background video' : 'Play background video'}
          </wa-button>
        </div>
      </section>
      <section id="business-info">
        <wa-card>

          <p>
            We build secure, accessible, high-performance web applications.
            Using Progressive Web Apps (PWA) and Backend as a Service (BaaS)
            platforms, we speed up development and scale with ease. Our
            database (Firebase, PostgreSQL) and authentication services stay
            reliable, monitored, and maintained over time.
          </p>
        </wa-card>
      </section>
      <!-- Intro copy and feature cards explain what the app is built with. -->
      <section id="features">
        <h2>
          Production: Progressive Web Apps (PWA) with
          <a
            href="https://www.pwabuilder.com"
            target="_blank"
            aria-current="page"
            >PWABuilder</a
          >.
        </h2>
        <p class="section-intro">
          This app follows best practices for security, accessibility, and
          performance to ensure a robust and reliable user experience.
        </p>
        <div id="features-grid">
          <wa-card class="feature-card">
            <wa-icon slot="header" name="bolt" label="Lightning bolt"></wa-icon>
            <h3>Lit Web Components</h3>
            <p>
              Small, fast, standards-based components with reactive
              @property/@state data binding -- no framework lock-in.
            </p>
          </wa-card>

          <wa-card class="feature-card">
            <wa-icon slot="header" name="palette" label="Palette"></wa-icon>
            <h3>Web Awesome UI</h3>
            <p>
              A full library of accessible, themeable wa-* components like
              wa-button, wa-input, and wa-card used across this entire app.
            </p>
          </wa-card>

          <wa-card class="feature-card">
            <wa-icon slot="header" name="wifi" label="WiFi"></wa-icon>
            <h3>Offline-First</h3>
            <p>
              Workbox precaches this app's assets so it keeps working even when
              the network drops -- see public/sw.js.
            </p>
          </wa-card>
        </div>
      </section>

      <!-- Each card maps an OWASP risk to a practice used by this app. -->
      <section id="security">
        <h2>Security: OWASP Top 10 in Practice</h2>
        <p class="section-intro">
          Every release is checked against the OWASP Top 10 risk categories.
          Here's how each one is addressed in this app:
        </p>

        <div id="security-gallery-viewport">
          <div id="security-grid">
            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A01</wa-tag>
              <h3>Broken Access Control</h3>
              <p>
                The Apps Script backend runs with "Execute as Me" and never
                exposes the underlying Sheet/Drive directly -- the client can
                only ever reach the single validated
                <code>doPost</code> endpoint.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A02</wa-tag>
              <h3>Cryptographic Failures</h3>
              <p>
                HTTPS is enforced end-to-end via Firebase Hosting, and a
                <code>Strict-Transport-Security</code> header (see
                firebase.json) tells browsers to never downgrade to
                plain HTTP.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A03</wa-tag>
              <h3>Injection</h3>
              <p>
                All contact-form input is re-validated and length-checked on the
                server in <code>doPost</code> before use -- the client's
                <code>required</code>/<code>pattern</code> attributes are UX
                only, never trusted as the security boundary.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A04</wa-tag>
              <h3>Insecure Design</h3>
              <p>
                Google reCAPTCHA v3 scores every submission server-side before
                an email is ever sent, mitigating automated abuse by design
                rather than as an afterthought.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A05</wa-tag>
              <h3>Security Misconfiguration</h3>
              <p>
                A locked-down <code>Content-Security-Policy</code>,
                <code>X-Frame-Options</code>, and
                <code>X-Content-Type-Options</code> are set globally in
                firebase.json, and no secrets ever ship in the client
                bundle.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A06</wa-tag>
              <h3>Vulnerable &amp; Outdated Components</h3>
              <p>
                Dependencies are pinned in package.json and regularly updated
                via <code>npm audit</code>, keeping Lit, Web Awesome, and
                Workbox current with upstream security patches.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A07</wa-tag>
              <h3>Identification &amp; Auth Failures</h3>
              <p>
                The reCAPTCHA secret key and Apps Script deployment stay
                strictly server-side -- there are no client-held credentials to
                leak or brute-force.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A08</wa-tag>
              <h3>Software &amp; Data Integrity Failures</h3>
              <p>
                reCAPTCHA tokens are independently re-verified against Google's
                <code>siteverify</code> API on the server, so a tampered or
                replayed client-side token is rejected.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A09</wa-tag>
              <h3>Logging &amp; Monitoring Failures</h3>
              <p>
                Failed submissions are logged with <code>console.error</code> on
                the client and captured in Apps Script's execution logs on the
                server for auditing.
              </p>
            </wa-card>

            <wa-card class="security-card">
              <wa-tag slot="header" variant="brand">A10</wa-tag>
              <h3>Server-Side Request Forgery</h3>
              <p>
                The Apps Script backend only ever calls one hardcoded Google
                endpoint (<code>siteverify</code>) -- it never fetches a
                user-supplied URL, closing off SSRF entirely.
              </p>
            </wa-card>
          </div>
        </div>
      </section>

      <!-- Cards summarize the accessibility choices visible across the app. -->
      <section id="accessibility">
        <h2>UI: Accessibility &amp; Usability</h2>
        <p class="section-intro">
          Beyond passing an automated audit, this app is built to be genuinely
          usable for everyone:
        </p>

        <div id="accessibility-grid">
          <wa-card class="a11y-card">
            <wa-icon
              slot="header"
              name="circle-half-stroke"
              label="Contrast"
            ></wa-icon>
            <h3>Contrast</h3>
            <p>
              Colors are drawn from Web Awesome's brand token scale (see
              global.css), which is tuned to meet WCAG AA contrast ratios in
              both the <code>wa-light</code> and <code>wa-dark</code> color
              schemes -- text never relies on color alone to convey meaning.
            </p>
          </wa-card>

          <wa-card class="a11y-card">
            <wa-icon slot="header" name="link" label="Links"></wa-icon>
            <h3>Links &amp; Navigation</h3>
            <p>
              Nav links use <code>aria-current="page"</code> to announce the
              active route to screen readers, and links are always underlined or
              otherwise distinguishable from surrounding text, never color-only.
            </p>
          </wa-card>

          <wa-card class="a11y-card">
            <wa-icon
              slot="header"
              name="hand-pointer"
              label="Interactive elements"
            ></wa-icon>
            <h3>Interactive Elements</h3>
            <p>
              Every form control has a visible <code>label</code>, buttons use
              Web Awesome's large touch-friendly hit targets, focus rings are
              never suppressed, and components like
              <code>wa-accordion</code> are fully operable with only a keyboard.
            </p>
          </wa-card>
        </div>
      </section>
      <!-- The image and text move at different speeds to create a parallax window. -->
      <section id="parallax">
        <h2>Reliability: Retrieval-Augmented Generation (RAG)</h2>
        <p class="section-intro">
          Retrieval-Augmented Generation (RAG) combines your AI model with
          internal and external knowledge sources, reducing hallucinations and
          improving response accuracy. We can provide a customized RAG solution
          tailored to your specific knowledge sources and AI model requirements.
        </p>
        <div id="parallax-frame">
          <div id="parallax-bg">
            <img
              src="./images/rag-img.jpeg"
              alt="A support agent in an 80's style showing a RAG concept on a PC."
            />
          </div>
          <div id="parallax-text">
            <h1>RAG increases trust and reliability!</h1>
          </div>
        </div>
      </section>
      <section id="apps-script">
        <h2>Google Apps Script</h2>
        <p class="section-intro">
          Our contact form uses Google Apps Script which allows us to integrate
          the message with Gmail. Google Apps Script allows you to automate
          tasks and extend the functionality of Google Workspace applications.
          You can create custom workflows, integrate with external APIs, and
          enhance your AI model's capabilities within the Google ecosystem.
        </p>
        <div id="apps-script-grid">
          <wa-card class="apps-script-card">
            <wa-icon
              slot="header"
              name="file"
              label="Google Apps Script"
            ></wa-icon>
            <h3>Google Docs</h3>
            <p>
              Google Docs can be enhanced with Google Apps Script to automate
              document creation, manage content, and integrate with other Google
              Workspace services.
            </p>
          </wa-card>
          <wa-card class="apps-script-card">
            <wa-icon
              slot="header"
              name="envelope"
              label="Google Apps Script"
            ></wa-icon>
            <h3>Google Gmail</h3>
            <p>
              Google Gmail can be enhanced with Google Apps Script to automate
              email handling, manage content, and integrate with other Google
              Workspace services.
            </p>
          </wa-card>
          <wa-card class="apps-script-card">
            <wa-icon
              slot="header"
              name="table"
              label="Google Apps Script"
            ></wa-icon>
            <h3>Google Sheets</h3>
            <p>
              Google Sheets can be enhanced with Google Apps Script to automate
              spreadsheet management, manage content, and integrate with other
              Google Workspace services.
            </p>
          </wa-card>
        </div>
      </section>
    `;
  }
}
